import { MutableRefObject, useEffect } from 'react';
import { Observable, PartialObserver, Subscription } from 'rxjs';
import { useSubscriptionInternal } from './internal';

/**
 * `useSubscription` allows you to subscribe to Observables using callbacks.
 *
 * It accepts an Observable and optional `next`, `error`, `complete` functions.
 * These functions must be provided in the correct order. Use `undefined` or `null`
 * for placeholders when you need to omit an argument.
 *
 * The Subscription will unsubscribe when the component unmounts, but you can also unsubscribe manually.
 *
 * ⚠ **Note:** To support concurrent mode, the subscription only happens
 * after the render is committed to the screen. This means all values will arrive
 * _after_ the first render, even if the Observable initial values synchronously.
 *
 * Changing callbacks will not trigger an emission. If you need to trigger an emission,
 * just create another Observable of the callback using {@link useObservable}.
 *
 * Like `useEffect`, you can access closure directly inside callbacks.
 * `useSubscription` will ensure the latest callback is called.
 *
 * When the Observable changes `useSubscription` will automatically
 * unsubscribe from the previous Observable and subscribe to the new one.
 *
 * ⚠ **Note:** Due to the design of RxJS, the Observable is killed once an error occurs in an observable.
 * You should prevent errors from reaching observables or `catchError` in sub-observables.
 * You can also set the observable in React state and replace it on error.
 * Then, `useSubscription` will automatically switch to the new Observable.
 *
 * @template TInput Input value within Observable.
 *
 * @param input$ Input Observable.
 * @param observer Observer
 */
export function useSubscription<TInput>(
  input$: Observable<TInput>,
  observer?: PartialObserver<TInput>,
): MutableRefObject<Subscription | undefined>;
/**
 * @template TInput Input value within Observable.
 *
 * @param input$ Input Observable.
 * @param next Notify when a new value is emitted.
 * @param error Notify when a new error is thrown.
 * @param complete Notify when the Observable is complete.
 */

export function useSubscription<TInput>(
  input$: Observable<TInput>,
  next?: ((value: TInput) => void) | null | undefined,
  error?: ((error: any) => void) | null | undefined,
  complete?: (() => void) | null | undefined,
): MutableRefObject<Subscription | undefined>;

export function useSubscription<TInput>(
  input$: Observable<TInput>,
  observerOrNext$?: PartialObserver<TInput> | ((value: TInput) => void) | null | undefined,
  error?: ((error: any) => void) | null | undefined,
  complete?: (() => void) | null | undefined,
): MutableRefObject<Subscription | undefined> {
  return useSubscriptionInternal(useEffect, [input$, observerOrNext$, error, complete]);
}
